import { createSlice, PayloadAction, createAsyncThunk } from "@reduxjs/toolkit";
import { LocaleState, Country, Currency } from "./types/locale-types";
import { RootState } from "../../redux/store";

const API_BASE = process.env.REACT_APP_API_URL || "";

async function handleResp(res: Response) {
  const text = await res.text();
  let payload: any = null;
  try {
    payload = text ? JSON.parse(text) : null;
  } catch {
    payload = text;
  }
  if (!res.ok) {
    const msg =
      (payload && (payload.message || payload.error)) ||
      res.statusText ||
      "Request failed";
    throw new Error(msg);
  }
  return payload;
}

export const fetchUserCountry = createAsyncThunk<
  { country: string; countryCode: string; countries?: Country[] },
  { silent?: boolean } | void,
  { rejectValue: string; state: RootState }
>("locale/fetchUserCountry", async (_, { rejectWithValue, getState }) => {
  const state = getState() as RootState;
  const token = (state as any).auth?.token;

  try {
    const url = new URL(`${API_BASE}/user-country`);
    const res = await fetch(url.toString(), {
      headers: { Authorization: `Bearer ${token}` },
    });
    const payload: any = await handleResp(res);
    // Extract data logic
    return payload;
  } catch (err: any) {
    // Log error when network or parsing fails
    console.error("Fetch Countries Network/Parsing Error:", err.message);
    return rejectWithValue(err?.message ?? "Failed to fetch countries");
  }
});

const initialState: LocaleState = {
  country: { items: [], loading: false, error: null },
  currency: { items: [], loading: false, error: null },
  selectedCountry: {
    id: 3,
    name: "Others",
    symbol: "USA",
    default_currency_id: 3,
  },
  selectedCurrency: {
    id: 3,
    name: "US Dollar (USD)",
    symbol: "$",
    exchange_rate: 1.0,
  },
  userLocation: { data: null, loading: false, error: null, attempted: false },
};

const localeSlice = createSlice({
  name: "locale",
  initialState,
  reducers: {
    setSelectedCountry(state, action: PayloadAction<Country>) {
      state.selectedCountry = action.payload;
      if (action.payload.currency) {
        state.selectedCurrency = action.payload.currency;
      }
    },
    setSelectedCurrency(state, action: PayloadAction<Currency>) {
      state.selectedCurrency = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUserCountry.pending, (state, action) => {
        const isSilent = Boolean(action.meta.arg?.silent);

        if (!isSilent) {
          state.country.loading = true;
          state.currency.loading = true;
          state.userLocation.loading = true;
          state.userLocation.attempted = false;
        }

        state.userLocation.error = null;
      })
      .addCase(fetchUserCountry.fulfilled, (state, action) => {
        state.country.loading = false;
        state.currency.loading = false;
        state.userLocation.loading = false;
        state.userLocation.data = action.payload;
        state.userLocation.attempted = true;

        if (Array.isArray(action.payload.countries)) {
          state.country.items = action.payload.countries;

          // Currency source now comes from countries payload only.
          const currencyMap = new Map<number, Currency>();
          action.payload.countries.forEach((item) => {
            if (item.currency) {
              currencyMap.set(item.currency.id, item.currency);
            }
          });
          state.currency.items = Array.from(currencyMap.values());
        }

        const detectedCode = action.payload.countryCode?.toLowerCase();
        if (
          detectedCode &&
          state.selectedCountry.id === 3 &&
          state.country.items.length > 0
        ) {
          const matchedCountry = state.country.items.find(
            (item) => item.symbol?.toLowerCase() === detectedCode,
          );

          if (matchedCountry) {
            state.selectedCountry = matchedCountry;
            if (matchedCountry.currency) {
              state.selectedCurrency = matchedCountry.currency;
            }
          }
        }
      })
      .addCase(fetchUserCountry.rejected, (state, action) => {
        state.country.loading = false;
        state.currency.loading = false;
        state.country.error =
          (action.payload as string) || "Failed to get countries";
        state.currency.error =
          (action.payload as string) || "Failed to get currencies";
        state.userLocation.loading = false;
        state.userLocation.attempted = true;
        state.userLocation.error =
          (action.payload as string) || "Failed to get user country";
      });
  },
});

export const { setSelectedCountry, setSelectedCurrency } = localeSlice.actions;
export default localeSlice.reducer;
